import type React from "react";

type MoveDirection = "next" | "prev" | "first" | "last";

type KeyboardNavigableItem = {
  id: string;
  option?: { label: string };
};

type UseDropdownKeyboardHandlersProps = {
  activeItemId: string | null;
  closeMenu: () => void;
  commitActiveItem: () => void;
  displayedMultiValueLength: number;
  filters: boolean;
  focusChipRemoveButton: (index: number) => void;
  focusInput: () => void;
  hasCustomValueOption: boolean;
  moveActiveItem: (direction: MoveDirection) => void;
  multiValueLength: number;
  navigableItems: readonly KeyboardNavigableItem[];
  onRemoveLastMultiValue: () => void;
  open: boolean;
  openMenu: () => void;
  query: string;
  removeChipAtIndex: (index: number, focusTarget: "input" | "chip") => void;
  setActiveItemId: (itemId: string) => void;
  setQuery: (query: string) => void;
  typeaheadPrefixRef: { current: string };
  typeaheadResetRef: { current: ReturnType<typeof setTimeout> | null };
};

const TYPEAHEAD_RESET_MS = 700;

export const useDropdownKeyboardHandlers = ({
  activeItemId,
  closeMenu,
  commitActiveItem,
  displayedMultiValueLength,
  filters,
  focusChipRemoveButton,
  focusInput,
  hasCustomValueOption,
  moveActiveItem,
  multiValueLength,
  navigableItems,
  onRemoveLastMultiValue,
  open,
  openMenu,
  query,
  removeChipAtIndex,
  setActiveItemId,
  setQuery,
  typeaheadPrefixRef,
  typeaheadResetRef,
}: UseDropdownKeyboardHandlersProps) => {
  // The controller keeps DOM focus while aria-activedescendant moves through
  // the listbox. Both select triggers and in-menu search inputs use this path,
  // so Arrow/Enter/Tab cannot drift between the two menu shapes.
  const handleActiveOptionKeyDown = (event: React.KeyboardEvent<HTMLElement>): boolean => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveActiveItem(event.key === "ArrowDown" ? "next" : "prev");
      return true;
    }

    if (event.key === "Enter" && open) {
      event.preventDefault();
      if (activeItemId || hasCustomValueOption) {
        commitActiveItem();
      }
      return true;
    }

    if (event.key === "Tab") {
      closeMenu();
      return true;
    }

    return false;
  };

  const handleSelectOnlyKeyDown = (event: React.KeyboardEvent<HTMLElement>): boolean => {
    if (filters) {
      return false;
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      moveActiveItem(event.key === "Home" ? "first" : "last");
      return true;
    }

    if (event.key === " " && typeaheadPrefixRef.current === "") {
      event.preventDefault();
      if (open && activeItemId) {
        commitActiveItem();
      } else {
        openMenu();
      }
      return true;
    }

    if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      const nextPrefix = `${typeaheadPrefixRef.current}${event.key}`.toLowerCase();
      typeaheadPrefixRef.current = nextPrefix;

      if (typeaheadResetRef.current != null) {
        clearTimeout(typeaheadResetRef.current);
      }
      typeaheadResetRef.current = setTimeout(() => {
        typeaheadPrefixRef.current = "";
      }, TYPEAHEAD_RESET_MS);

      const match = navigableItems.find((item) =>
        item.option?.label.toLowerCase().startsWith(nextPrefix),
      );
      if (match) {
        openMenu();
        setActiveItemId(match.id);
      }
      return true;
    }

    return false;
  };

  const handleSearchableSelectTriggerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      openMenu();
      return;
    }

    if (event.key === "Escape") {
      event.stopPropagation();
      closeMenu();
      return;
    }

    if (event.key.length === 1 && event.key !== " " && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      event.stopPropagation();
      setQuery(event.key);
      openMenu();
    }
  };

  const handleMenuSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (handleActiveOptionKeyDown(event)) {
      event.stopPropagation();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
      focusInput();
      return;
    }
  };

  const handleInputNavigationKeyDown = (event: React.KeyboardEvent<HTMLElement>): boolean => {
    if (handleActiveOptionKeyDown(event)) {
      return true;
    }

    if (handleSelectOnlyKeyDown(event)) {
      return true;
    }

    if (event.key === "Escape") {
      closeMenu();
      return true;
    }

    return false;
  };

  const handleSingleInputKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (handleInputNavigationKeyDown(event)) {
      event.stopPropagation();
    }
  };

  const handleMultiInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowLeft" && query === "" && displayedMultiValueLength > 0) {
      event.preventDefault();
      event.stopPropagation();
      focusChipRemoveButton(displayedMultiValueLength - 1);
      return;
    }

    if (handleInputNavigationKeyDown(event)) {
      event.stopPropagation();
      return;
    }

    if (event.key === "Backspace" && query === "" && multiValueLength > 0) {
      event.stopPropagation();
      onRemoveLastMultiValue();
    }
  };

  const handleChipRemoveButtonKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      event.stopPropagation();
      removeChipAtIndex(index, "chip");
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      focusInput();
      moveActiveItem(event.key === "ArrowDown" ? "next" : "prev");
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      event.stopPropagation();
      if (index > 0) {
        focusChipRemoveButton(index - 1);
      }
      return;
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      event.stopPropagation();
      if (index < displayedMultiValueLength - 1) {
        focusChipRemoveButton(index + 1);
      } else {
        focusInput();
      }
    }
  };

  return {
    handleChipRemoveButtonKeyDown,
    handleMenuSearchKeyDown,
    handleMultiInputKeyDown,
    handleSearchableSelectTriggerKeyDown,
    handleSingleInputKeyDown,
  };
};
